import { Route, Routes } from 'react-router-dom';
import ChatWindow from './pages/ChatWindow.jsx';
import TagManager from 'react-gtm-module';
import React, { useState, useEffect } from 'react';
import './services/i18n';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import axios from './utils/axiosConfig';
import Loader from './components/Loader.jsx';
import toast, { Toaster } from 'react-hot-toast';
import { loadGoogleFont } from './utils/googleFont.js';
import { analytic } from './utils/Analytics.js';

const gtmID = process.env.REACT_APP_GTM_ID;

const tagManagerArgs = {
    gtmId: gtmID
}

// TagManager.initialize(tagManagerArgs);

const App = () => {

    const { t, i18n } = useTranslation();

    const ASSETS_URL = process.env.REACT_APP_ASSETS_URL;
    const apiURL = process.env.REACT_APP_API_URL;
    const apiKey = process.env.REACT_APP_BOT_API_KEY;

    const [verified, setVerified] = useState(false);
    const [iframeDomain, setIframeDomain] = useState('');
    const [view, setView] = useState('narrow');
    const [domains, setDomains] = useState(null);
    const [domainFromIframeMessage, setDomainFromIframeMessage] = useState();
    const [publicEnabled, setPublicEnabled] = useState(false);
    const [widgetEnabled, setWidgetEnabled] = useState(false);
    const [isInIframe, setIsInIframe] = useState(false);
    const [loader, setLoader] = useState(true);
    const [loadWidget, setWidgetLoad] = useState(false);
    // const [botResponse, setBotResponse] = useState(null);
    const [error, setError] = useState(false);

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    let botId = urlParams?.get("bot");

    if (botId && botId.startsWith('bot_')) {
        botId = botId.substring(4);
    }

    const language = urlParams?.get('language');
    const color1 = urlParams?.get('primary');
    const darkColor1 = urlParams?.get('primaryDark');
    const defaultView = urlParams?.get('view');
    const showDisclaimer = urlParams?.get('showDisclaimer') ? urlParams?.get('showDisclaimer') === 'true' : true;
    const domain = urlParams?.get('domain') || null;
    const conversationId = urlParams?.get('conversation') || null;
    const theme = urlParams?.get('theme') || null;
    const family = urlParams?.get('family') || null;
    const signature = urlParams?.get('signature') || null;
    const timestamp = urlParams?.get('timestamp') || null;
    const key = urlParams?.get('org_key') || null;

    const showVoiceMode = urlParams?.get('voiceMode') ? urlParams?.get('voiceMode') === 'true' : false;
    const previewMode = urlParams?.get('preview') ? urlParams?.get('preview') === 'true' : null;

    const localLanguage = localStorage.getItem('language');

    const handlePostMessage = (event) => {

        if (event.origin.includes(domain) || domain == 'localhost:3000') {
            setDomainFromIframeMessage(event?.data?.domain ? event?.data?.domain : domain);
        }

        if (event.origin.includes(domain) || domain == 'localhost:3000') {
            if (event.data.view) {
                setView(event.data.view)
            }

            if (event.data.chatId) {
                localStorage.setItem(`chatId_${botId}`, event.data.chatId);
            }

            if (event.data.clearChat) {
                localStorage.removeItem(`chatId_${botId}`);
                localStorage.removeItem('language');
                localStorage.removeItem(`initialLanguageSet_${botId}`);
            }

            if (event.data.loadWidget) {
                setWidgetLoad(true);
            }

            if(Object.hasOwn(event.data, 'widgetEnabled')){
                setWidgetEnabled(event.data.widgetEnabled);
            }
      }
    };

    const fetchDomains = async () => {
        // if(!isInIframe) return;

        await axios({
            url: `${apiURL}/admin/widget_domains/bot/bot_${botId}`,
            method: 'GET',
            headers: {
                'Api-token': apiKey
            }
        }).then((response) => {
            if (response.status == 200) {

                const whitelistedDomains = response.data.widget_data;
                const botConfig = response.data.bot_config;

                setDomains(whitelistedDomains);

                for (let i = 0; i < whitelistedDomains.length; i++) {
                    if ((whitelistedDomains[i].attributes.Domain == domain && whitelistedDomains[i].attributes.Domain == domainFromIframeMessage) || (whitelistedDomains[i].attributes.Domain == '*')) {
                        setIframeDomain(domain);
                        setVerified(true);

                        if (!loader) {
                            setLoader(false);
                        }

                        setPublicEnabled(botConfig.enable_chat_page);
                        setWidgetEnabled(botConfig.enable_chat_widget);

                        const measurementId = botConfig.measurement_id || process.env.REACT_APP_MEASUREMENT_ID;

                        if (!domain || domain?.includes('creator')) {
                            ReactGA.initialize(measurementId);
                        }
                    }
                }
            }

        }).catch((err) => {
            console.log("Error fetching domains ======>", err);
            setError(true);
        })
    }

    useEffect(() => {
        fetchDomains();
    }, [])

    useEffect(() => {

        setView(defaultView);

        // fetchInfo();

        setIsInIframe(window.self !== window.top);

        if (theme == 'dark') {
            document.documentElement.classList.add(theme);
        }

        if (family) {
            loadGoogleFont(family);
        } else if (theme == 'dark') {
            loadGoogleFont('DM Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000');
        }

        window.addEventListener('message', handlePostMessage);

        return () => {
            window.removeEventListener('message', handlePostMessage);
        };
    }, []);

    useEffect(() => {

        i18n.changeLanguage(localLanguage ? localLanguage : language ? language : "en");

        if (!localLanguage) {
            localStorage.setItem('language', language ? language : 'en')
        }

    }, [language])

    useEffect(() => {
        window.parentDomain = domain;
    }, [domain]);
  
    if (((publicEnabled && !isInIframe) || (verified && (previewMode ? true : widgetEnabled) && loadWidget)) && botId && botId !== "null") {
        return (
            <Routes>
                <Route 
                    path='/' 
                    element={
                        <ChatWindow 
                            iframeDomain={iframeDomain} 
                            botApiId={botId} 
                            view={view} 
                            primaryColor={color1 || darkColor1 ? `#${theme == 'dark' ? darkColor1 || '2c2c78' : color1}` : theme == 'dark' ? '#2c2c78' : '#263FA9'} 
                            showDisclaimer={showDisclaimer} 
                            showVoiceMode={showVoiceMode} 
                            publicMode={publicEnabled} 
                            previewMode={previewMode}
                            conversationId={conversationId} 
                            theme={theme} 
                        />
                    } 
                />
            </Routes>
        )
    }

    return (
        <>
            {
                !error && (
                    <Loader primary={color1 || darkColor1 ? `#${theme == 'dark' ? darkColor1 || '#2c2c78' : color1}` : theme == 'dark' ? '#2c2c78' : '#263FA9'} showLoader={loader} />
                )
            }
            <Toaster
                toastOptions={{
                    style: {
                        background: theme == 'dark' ? '#232326' : '#ffffff',
                        color: theme == 'dark' ? '#ffffff' : '#000000',
                    }
                }}
            />
        </>
    );

}

export default App;